import { useRef } from 'react';
import { defaultMemoize } from 'reselect';
import { BaseStore } from '../../types';

/**
 * Create selector hook that supports memoized RESELECT selectors
 */

type SelectorCreator<Args extends unknown[], State, Result> = (
  ...args: Args
) => (state: State) => Result;

export const createUseSelector =
  <State>(useStore: BaseStore<State>) =>
  <Result, Args extends unknown[] = []>(
    selectorCreator: SelectorCreator<Args, State, Result>,
    args: Args = [] as unknown as Args,
    options?: Parameters<BaseStore<State>>[1]
  ): Result => {
    // Create memoized version of selector creator and store it as permanent ref, so its cache would live while component lives. This is needed for parametrized selectors.
    const selCreator = useRef(defaultMemoize(selectorCreator));

    // Create actual selector by calling creator with variable args. If args don't change during update - this function won't be recreated due to memoize and won't recompute anything as a result.
    const selector = selCreator.current(...args);

    // NB In theory, we can use simple useMemo instead of defaultMemoize/useRef. The difference is though we'll have to care about reference stability of selectorCreator itself, which is not good. `defaultMemoize`, on the other hand, takes care of this problem.
    //  The other problem is that `args` reference is always unstable and must be spread into dependencies of `useMemo` which is also not good.
    // const selector = useMemo(
    //   () => selectorCreator(...args),
    //   [selectorCreator, ...args]
    // );

    // Select actual data
    return useStore(selector, options);
  };
